/**
 * Pagination — prev/next + page numbers with ellipsis
 *
 * Supports both controlled (server-side) and display-only modes.
 *
 * @example
 * <Pagination
 *   page={currentPage}           // 1-indexed
 *   totalPages={Math.ceil(total / limit)}
 *   onPageChange={(p) => setPage(p)}
 *   showInfo
 *   from={(currentPage - 1) * limit + 1}
 *   to={Math.min(currentPage * limit, total)}
 *   total={total}
 * />
 */
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Returns an array of page numbers + 'ellipsis' markers.
 * E.g. [1, 'start-ellipsis', 5, 6, 7, 'end-ellipsis', 20]
 */
function buildPageRange(page, totalPages, siblingCount = 1) {
  const left  = page - siblingCount;        // leftmost sibling
  const right = page + siblingCount;        // rightmost sibling

  const pages = [];

  // Always include first & last; fill middle range
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= left && i <= right)
    ) {
      pages.push(i);
    }
  }

  // Insert ellipsis markers where there are gaps
  const result = [];
  let prev = null;
  for (const p of pages) {
    if (prev !== null && p - prev > 1) {
      result.push(p - prev === 2 ? prev + 1 : (prev === 1 ? 'start-ellipsis' : 'end-ellipsis'));
    }
    result.push(p);
    prev = p;
  }
  return result;
}

// ── PageButton ─────────────────────────────────────────────────────────────────

function PageButton({ label, active, disabled, onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || String(label)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'h-8 min-w-[32px] px-2 rounded-lg border-2 text-xs font-bold transition-all select-none',
        active
          ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary-shadow,#0284C7)]'
          : 'bg-white text-[var(--text-main)] border-[var(--border-light)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[var(--border-light)] disabled:hover:text-[var(--text-main)]',
      )}
    >
      {label}
    </button>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────────

export default function Pagination({
  page,
  totalPages,
  onPageChange,
  showInfo,
  from,
  to,
  total,
  loading,
  className,
  siblingCount,
}) {
  const { t } = useLanguage();

  const pages = useMemo(
    () => buildPageRange(page, totalPages, siblingCount),
    [page, totalPages, siblingCount],
  );

  if (totalPages <= 1 && !showInfo) return null;

  const infoText = showInfo && total != null
    ? t('admin.pagination_info')
        .replace('{start}', String(from ?? ((page - 1) * 20 + 1)))
        .replace('{end}',   String(to   ?? Math.min(page * 20, total)))
        .replace('{total}', String(total))
    : null;

  return (
    <div
      className={cn('flex flex-wrap items-center justify-between gap-2 mt-3 px-1', className)}
      aria-label={t('admin.pagination_label') || 'Pagination'}
    >
      {/* Info text (left) */}
      <span className="text-xs text-[var(--text-muted)] font-medium min-w-0">
        {infoText ?? ''}
      </span>

      {/* Controls (right) */}
      <div className="flex items-center gap-1">
        {/* Prev */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page - 1)}
          disabled={loading || page <= 1}
          aria-label={t('admin.prev_page')}
          className="h-8 w-8 p-0 rounded-lg border-2"
        >
          <ChevronLeft size={14} />
        </Button>

        {/* Page numbers */}
        {totalPages > 1 && pages.map((p, idx) =>
          typeof p === 'string' ? (
            <span key={`${p}-${idx}`} className="h-8 w-8 flex items-center justify-center">
              <MoreHorizontal size={12} className="text-[var(--text-muted)]" />
            </span>
          ) : (
            <PageButton
              key={p}
              label={p}
              active={p === page}
              disabled={loading}
              onClick={() => onPageChange?.(p)}
            />
          )
        )}

        {/* Next */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page + 1)}
          disabled={loading || page >= totalPages}
          aria-label={t('admin.next_page')}
          className="h-8 w-8 p-0 rounded-lg border-2"
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

Pagination.propTypes = {
  /** Current page (1-indexed) */
  page: PropTypes.number.isRequired,
  /** Total number of pages */
  totalPages: PropTypes.number.isRequired,
  /** Called with new page number (1-indexed) */
  onPageChange: PropTypes.func,
  /** Show "X-Y of Z" info text */
  showInfo: PropTypes.bool,
  /** First item index on current page */
  from: PropTypes.number,
  /** Last item index on current page */
  to: PropTypes.number,
  /** Total record count */
  total: PropTypes.number,
  /** Disable buttons while fetching */
  loading: PropTypes.bool,
  className: PropTypes.string,
  /** Number of sibling pages shown around current (default 1) */
  siblingCount: PropTypes.number,
};

Pagination.defaultProps = {
  showInfo: false,
  loading: false,
  siblingCount: 1,
};
